import React, { useRef, useEffect } from 'react';
import Cookies from 'js-cookie';
import { useFileUploader } from '@panel/commons/hooks';
import { useDrop, useDrag } from 'react-dnd';
import { useLocation } from 'react-router-dom';
import { isMobileDevice } from '@panel/commons/helpers/device';
import {
  ImageBoxContainer,
  MainMarker,
  ImageBorderWrapper,
  Image,
  Editable,
  Controls,
  Rotator,
  Deletor,
  MainSelector
} from './styles';
import LoadSpinner from './loadspinner';
import defaultImg from '../../../../../assets/img/defaultImg.png';
import Overlay from './overlay';

const type = 'Item';

const Status = Object.freeze({
  IDLE: 'IDLE',
  LOADING: 'LOADING',
  ERROR: 'ERROR',
  SUCCESS: 'SUCCESS'
});

function Item({
  item,
  onDelete,
  onRotate,
  onTitle,
  onUrl,
  index,
  moveItem,
  onError,
  mainMarker = false,
  mainMarkerTitle = '',
  editablePlaceholder = '',
  hasMain = false,
  onMainSelect,
  onStatusChange = () => {},
  keysErrors
}) {
  const location = useLocation();
  const { loading, response, error, startUpload } = useFileUploader();
  const isMobile = isMobileDevice();
  const ref = useRef(null);
  const srcUrl = item.url || item.temporalUrl || defaultImg;

  useEffect(() => {
    let status;
    if (loading) {
      status = Status.LOADING;
    } else if (error) {
      status = Status.ERROR;
    } else if (response !== null) {
      status = Status.SUCCESS;
    } else {
      status = Status.IDLE;
    }
    if (item) {
      onStatusChange(item, status);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, response]);

  useEffect(() => {
    if (!item.temporalUrl && !item.url && !loading && (!response && !error)) {
      if (!(item.error && item.error.subtitle.includes('format'))) {
        const params = new URLSearchParams(location.search);
        const url = `${
          process.env.REACT_APP_API_PUBLICADOR
        }/publicador/api/v1/multimedia/preview?postingId=${params.get('postingId')}`;
        const headers = {
          'x-panel-portal': process.env.REACT_APP_BUILD_SITE,
          sessionId: Cookies.get('sessionId')
        };
        const { file } = item;
        startUpload({ url, file, headers });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  useEffect(() => {
    if (error && typeof onError === 'function') {
      const msg =
        error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : keysErrors.generic;
      onError(item.id, msg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (response && typeof onUrl === 'function') {
      const { temporalUrl, fullName } = response;
      onUrl(item.id, { temporalUrl, fullName, file: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  const [, drop] = useDrop({
    accept: type,
    hover(dragItem) {
      if (!ref.current) {
        return;
      }
      const dragIndex = dragItem.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      moveItem(dragIndex, hoverIndex);
      dragItem.index = hoverIndex;
    }
  });

  const [{ isDragging }, drag] = useDrag({
    item: { type, id: item.id, index },
    canDrag: !isMobile,
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });

  const getTitle = evt => {
    const { target } = evt;
    if (target) {
      onTitle(item.id, target.value);
    }
  };

  drag(drop(ref));

  return (
    <ImageBoxContainer
      data-repa={`step2-${item.multimediaType === 'IMAGE' ? 'images' : 'plans'}-itemlist-item-${
        item.order
      }-ImageBoxContainer`}
      ref={ref}
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      {!loading && item.error && <Overlay item={item} />}
      {loading && <LoadSpinner />}
      {srcUrl && (
        <Controls
          data-repa={`step2-${item.multimediaType === 'IMAGE' ? 'images' : 'plans'}-itemlist-item-${
            item.order
          }-controls`}
        >
          {index !== 0 && hasMain && isMobile && !item.error && (
            <MainSelector
              data-repa={`step2-${item.multimediaType === 'IMAGE' ? 'images' : 'plans'}-itemlist-item-${
                item.order
              }-button-mainSelector`}
              onClick={onMainSelect}
            />
          )}
          {!item.error && (
            <Rotator
              data-repa={`step2-${item.multimediaType === 'IMAGE' ? 'images' : 'plans'}-itemlist-item-${
                item.order
              }-button-rotator`}
              onClick={() => onRotate(item.id)}
            />
          )}
          <Deletor
            data-repa={`step2-${item.multimediaType === 'IMAGE' ? 'images' : 'plans'}-itemlist-item-${
              item.order
            }-button-deletor`}
            onClick={() => onDelete(item.id)}
          />
        </Controls>
      )}
      {mainMarker && srcUrl && index === 0 && (
        <MainMarker
          data-repa={`step2-${item.multimediaType === 'IMAGE' ? 'images' : 'plans'}-itemlist-item-${
            item.order
          }-mainMarker`}
        >
          <div />
          {mainMarkerTitle}
        </MainMarker>
      )}
      {!loading && srcUrl && (
        <>
          <ImageBorderWrapper
            data-repa={`step2-${item.multimediaType === 'IMAGE' ? 'images' : 'plans'}-itemlist-item-${
              item.order
            }-imageBorderWrapper`}
          >
            <Image
              rotationAngle={item.angle}
              src={srcUrl}
              data-repa={`step2-${item.multimediaType === 'IMAGE' ? 'images' : 'plans'}-itemlist-item-${
                item.order
              }-editable-img`}
            />
          </ImageBorderWrapper>
          <Editable
            data-repa={`step2-${item.multimediaType === 'IMAGE' ? 'images' : 'plans'}-itemlist-item-${
              item.order
            }-editable`}
            type='text'
            onBlur={getTitle}
            defaultValue={item.title}
            placeholder={editablePlaceholder}
            maxLength={50}
          />
        </>
      )}
    </ImageBoxContainer>
  );
}

export { Item };
